import { z } from 'zod';
import { parseCOPdeAPI } from '../money.js';
import { fechaContable } from '../fecha-contable.js';

/**
 * La puerta de entrada del cliente al `jsonb` que arma `rep.tablero` (fase 11, tarea 10).
 *
 * La autoridad sigue estando en Postgres. Esto no recalcula nada: decide si el objeto que llegó es
 * seguro de usar antes de que una pantalla lo toque. Un tablero mal formado que se dibuja a medias
 * es peor que uno que no se dibuja, porque el que no se dibuja se ve.
 *
 * TODO `_cop` PASA POR `parseCOPdeAPI` Y ACEPTA TEXTO O NÚMERO. Un `bigint` de Postgres llega como
 * texto por PostgREST, pero dentro de un `jsonb` llega como número nativo, y este objeto viaja por
 * las dos vías: `rpc('tablero')` lo devuelve como `jsonb`, y una columna suelta del mismo dato
 * llegaría como texto. Aceptar los dos cuesta una unión y evita el fallo que solo aparece cuando
 * alguien cambia la forma de pedirlo.
 */
const copDesdeAPI = z.union([z.string(), z.number()]).transform(parseCOPdeAPI);

/**
 * El margen bruto viaja en PORCENTAJE (52.5), nunca en fracción (0.525). Es la única llave del
 * tablero sin sufijo `_cop`, y la regla de la casa para los porcentajes. Acepta texto por lo mismo
 * que los montos: un `numeric` de Postgres es texto por PostgREST fuera de un `jsonb`.
 */
const porcentajeDesdeAPI = z.union([z.string(), z.number()]).transform((v) => Number(v));

export const esquemaIngresoPorFuente = z.object({
  fuente: z.string(),
  bruto_cop: copDesdeAPI,
});

export const esquemaGastoPorCategoria = z.object({
  categoria_codigo: z.string(),
  categoria_nombre: z.string(),
  // Solo `costo` y `gasto`, y no los tres del enum de la base. `rep.gastos_por_categoria` filtra por
  // esas dos naturalezas, así que un `ingreso` acá no sería un caso raro sino un defecto del
  // servidor, y lo correcto es que el parseo lo rechace en vez de que la pantalla lo dibuje como un
  // gasto más. Adjudicación 28.1: la naturaleza es propiedad de la cuenta, no del nombre.
  naturaleza_resultado: z.enum(['costo', 'gasto']),
  comportamiento: z.enum(['fijo', 'variable']).nullable(),
  monto_cop: copDesdeAPI,
});

export const esquemaPicoDia = z.object({
  fecha_contable: z.string(),
  ingresos_cop: copDesdeAPI,
  gastos_cop: copDesdeAPI,
});

export const esquemaPicoSemana = z.object({
  semana_iso: z.string(),
  ingresos_cop: copDesdeAPI,
  gastos_cop: copDesdeAPI,
});

export const esquemaPromedioDiaSemana = z.object({
  dia_semana_iso: z.number().int().min(1).max(7),
  promedio_cop: copDesdeAPI,
  muestras: z.number().int().nonnegative(),
});

export const esquemaSaldosYPasivos = z.object({
  caja_menor_cop: copDesdeAPI,
  caja_ahorro_cop: copDesdeAPI,
  banco_cop: copDesdeAPI,
  pasivo_impoconsumo_cop: copDesdeAPI,
  pasivo_retefuente_cop: copDesdeAPI,
  pasivo_reteica_cop: copDesdeAPI,
  pasivo_propinas_efectivo_cop: copDesdeAPI,
  pasivo_propinas_transferencia_cop: copDesdeAPI,
});

export const esquemaCapital = z.object({
  aporte_cop: copDesdeAPI,
  retiro_cop: copDesdeAPI,
  neto_cop: copDesdeAPI,
});

/**
 * Las dieciséis llaves de `rep.tablero`.
 *
 * `pico_dia` Y `pico_semana` SON NULLABLE, y esto no es defensa preventiva: se midió contra la
 * instancia. Un mes sin un solo movimiento devuelve las dieciséis llaves igual, con ceros y listas
 * vacías, pero esas dos vienen en `null`, porque no hay un día de mayor movimiento cuando no hubo
 * ninguno. Un cero ahí sería peor que un nulo: la pantalla dibujaría un día con ventas en cero como
 * si ese día hubiera existido. El bloque del plan las declaraba obligatorias, y con eso el tablero
 * de cualquier mes futuro habría reventado el parseo antes de dibujar nada.
 *
 * LA LLAVE DE CATEGORÍAS SE LLAMA `gastos_y_costos_por_categoria` y no `gastos_por_categoria`, que
 * es lo que el bloque esperaba. Adjudicación 244: al lado de `gastos_cop` no suma lo mismo, y las
 * dos cifras son correctas, porque la lista incluye las categorías que son costo por naturaleza. El
 * nombre lo dice donde el dato nace.
 *
 * EL OBJETO NO ES `strict()` a propósito. Una llave nueva que el servidor agregue tiene que poder
 * llegar sin romper un cliente que todavía no la conoce: el orden de despliegue es migraciones,
 * funciones y después la aplicación, así que durante ese rato el servidor va por delante.
 */
export const esquemaTablero = z.object({
  mes: z.string().transform(fechaContable),
  venta_bruta_cop: copDesdeAPI,
  descuento_cop: copDesdeAPI,
  ingresos_cop: copDesdeAPI,
  costo_ventas_cop: copDesdeAPI,
  margen_bruto_cop: copDesdeAPI,
  margen_bruto_pct: porcentajeDesdeAPI,
  gastos_cop: copDesdeAPI,
  utilidad_cop: copDesdeAPI,
  ingresos_por_fuente: z.array(esquemaIngresoPorFuente),
  gastos_y_costos_por_categoria: z.array(esquemaGastoPorCategoria),
  pico_dia: esquemaPicoDia.nullable(),
  pico_semana: esquemaPicoSemana.nullable(),
  promedio_por_dia_semana: z.array(esquemaPromedioDiaSemana),
  saldos_y_pasivos: esquemaSaldosYPasivos,
  capital: esquemaCapital,
});

export type Tablero = z.infer<typeof esquemaTablero>;
export type IngresoPorFuente = z.infer<typeof esquemaIngresoPorFuente>;
export type GastoPorCategoria = z.infer<typeof esquemaGastoPorCategoria>;
export type SaldosYPasivos = z.infer<typeof esquemaSaldosYPasivos>;

export function parseTablero(json: unknown): Tablero {
  return esquemaTablero.parse(json);
}
